import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, Upload, Radio } from 'lucide-react';
import { mediaAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

// Shared admin screen for reels, live streams and podcast episodes.
// Whatever is published here is exactly what the website and both apps show;
// drafts (is_active false) stay private to staff.

const YOUTUBE_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

export const youtubeId = (url) => (url || '').match(YOUTUBE_RE)?.[1] || null;
export const youtubeThumb = (url) => {
  const id = youtubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
// The apps play these URLs directly, so uploads must be absolute.
const absoluteUrl = (url) => {
  if (!url || /^https?:\/\//.test(url)) return url;
  return `${API_BASE || window.location.origin}${url}`;
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Drafts' },
];

const inputClass = 'w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#B3261E]/30';

function emptyForm(fields) {
  return Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === 'switch' ? false : '')]));
}

function UploadButton({ accept, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mediaAPI.upload(fd);
      onUploaded(absoluteUrl(res.data?.cdn_url || res.data?.url));
      toast.success('Uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`inline-flex items-center gap-1.5 shrink-0 px-3 py-2 rounded-lg border border-[#E7E0D6] text-sm cursor-pointer hover:bg-[#FBF7EF] ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      Upload
      <input type="file" accept={accept} className="hidden" onChange={onChange} />
    </label>
  );
}

function Field({ field, value, onChange }) {
  const id = `media-field-${field.name}`;
  const label = (
    <label htmlFor={id} className="text-sm font-medium block mb-1">
      {field.label}{field.required && <span className="text-[#B3261E]"> *</span>}
    </label>
  );
  if (field.type === 'switch') {
    return (
      <label className="flex items-start gap-2 cursor-pointer text-sm">
        <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 rounded" />
        <span>
          <span className="font-medium">{field.label}</span>
          {field.help && <span className="block text-xs text-[#5B616E]">{field.help}</span>}
        </span>
      </label>
    );
  }
  return (
    <div>
      {label}
      {field.type === 'textarea' ? (
        <textarea id={id} rows={3} value={value} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} className={`${inputClass} resize-none`} />
      ) : (
        <div className="flex gap-2">
          <input
            id={id}
            type={field.type === 'number' ? 'number' : 'text'}
            min={field.type === 'number' ? 0 : undefined}
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
          {field.upload && <UploadButton accept={field.upload} onUploaded={onChange} />}
        </div>
      )}
      {field.help && <p className="text-xs text-[#5B616E] mt-1">{field.help}</p>}
    </div>
  );
}

function Preview({ form, config }) {
  const thumb = config.thumbnail(form);
  const audio = config.audioField && form[config.audioField];
  if (!thumb && !audio) return null;
  return (
    <div className="rounded-xl border border-[#E7E0D6] bg-[#FBF7EF] p-3 space-y-3">
      {thumb && <img src={thumb} alt="" className="w-full max-h-48 object-cover rounded-lg bg-black/5" />}
      {audio && <audio controls src={audio} className="w-full" preload="none" />}
    </div>
  );
}

/**
 * @param {object} config
 * @param {string} config.title            Page heading.
 * @param {string} config.description      Subtitle under the heading.
 * @param {string} config.noun             Singular item name ("reel").
 * @param {object} config.api              { list, create, update, delete } from lib/api.
 * @param {Array}  config.fields           Form fields: { name, label, type, required, placeholder, help, upload, default, validate }.
 * @param {(item) => string|null} config.thumbnail
 * @param {(item) => string} [config.subtitle]  Secondary line on each row.
 * @param {string} [config.audioField]     Field holding an audio URL to preview.
 * @param {string} [config.liveField]      Boolean field shown as a "Live" badge.
 * @param {React.ComponentType} config.icon
 */
export default function MediaContentManager({ config }) {
  const { title, description, noun, api, fields, icon: Icon } = config;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null = closed, {} = new, item = edit
  const [form, setForm] = useState(() => emptyForm(fields));
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.list(false));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Couldn't load ${noun}s`);
    } finally {
      setLoading(false);
    }
  }, [api, noun]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    published: items.filter((i) => i.is_active).length,
    draft: items.filter((i) => !i.is_active).length,
  }), [items]);

  const visible = items.filter((i) => filter === 'all' || (filter === 'published' ? i.is_active : !i.is_active));

  const openNew = () => {
    setForm(emptyForm(fields));
    setEditing({});
  };

  const openEdit = (item) => {
    setForm(Object.fromEntries(fields.map((f) => [f.name, item[f.name] ?? (f.type === 'switch' ? false : '')])));
    setEditing(item);
  };

  const save = async (publish) => {
    for (const f of fields) {
      const v = form[f.name];
      if (f.required && !String(v ?? '').trim()) { toast.error(`${f.label} is required`); return; }
      const problem = f.validate?.(v);
      if (problem) { toast.error(problem); return; }
    }
    const payload = { ...form, is_active: publish };
    fields.filter((f) => f.type === 'number').forEach((f) => { payload[f.name] = Number(payload[f.name] || 0); });
    setSaving(true);
    try {
      if (editing?.id) await api.update(editing.id, payload);
      else await api.create(payload);
      toast.success(publish ? `${title.replace(/s$/, '')} published` : 'Saved as draft');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      await api.update(item.id, { is_active: !item.is_active });
      toast.success(item.is_active ? 'Unpublished — now a draft' : 'Published');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const confirmDelete = async () => {
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(item.id);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-sm text-[#5B616E] mt-1">{description}</p>
        </div>
        <Button className="bg-[#B3261E] text-white hover:bg-[#9E1F19]" onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" /> New {noun}
        </Button>
      </div>

      <div className="flex gap-2" role="tablist" aria-label={`Filter ${noun}s`}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === f.key ? 'bg-[#B3261E] text-white border-[#B3261E]' : 'bg-white border-[#E7E0D6] text-[#5B616E] hover:bg-[#FBF7EF]'}`}
          >
            {f.label} <span className="opacity-70">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : visible.length === 0 ? (
        <Card className="bg-white border-[#E7E0D6] p-12 text-center">
          <Icon className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
          <p className="text-[#5B616E]">
            {items.length === 0 ? `No ${noun}s yet. Published ${noun}s appear on the website and in the apps.` : `Nothing in ${FILTERS.find((f) => f.key === filter).label.toLowerCase()}.`}
          </p>
          {items.length === 0 && (
            <Button className="mt-4 bg-[#B3261E] text-white hover:bg-[#9E1F19]" onClick={openNew}>
              <Plus className="w-4 h-4 mr-2" /> Add the first {noun}
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((item) => {
            const thumb = config.thumbnail(item);
            return (
              <Card key={item.id} className="bg-white border-[#E7E0D6]">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-full sm:w-36 aspect-video rounded-lg overflow-hidden bg-[#F3EEE6] shrink-0 flex items-center justify-center">
                      {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Icon className="w-8 h-8 text-[#C9BFAF]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{item.title}</h3>
                      {config.subtitle && <p className="text-xs text-[#5B616E] mt-0.5 truncate">{config.subtitle(item)}</p>}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge className={`text-[10px] ${item.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {item.is_active ? 'Published' : 'Draft'}
                        </Badge>
                        {config.liveField && item[config.liveField] && (
                          <Badge className="text-[10px] bg-red-50 text-[#B3261E]"><Radio className="w-3 h-3 mr-1" />Live now</Badge>
                        )}
                        {item.created_at && <span className="text-xs text-[#5B616E]">{new Date(item.created_at).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => togglePublished(item)}>
                        {item.is_active ? <><EyeOff className="w-4 h-4 mr-1" />Unpublish</> : <><Eye className="w-4 h-4 mr-1" />Publish</>}
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Edit ${item.title}`} onClick={() => openEdit(item)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" aria-label={`Delete ${item.title}`} className="text-red-600" onClick={() => setPendingDelete(item)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && !saving && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? `Edit ${noun}` : `New ${noun}`}</DialogTitle>
            <DialogDescription>Publish to show it on the website and in the apps, or save it as a draft.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4 mt-2" onSubmit={(e) => { e.preventDefault(); save(true); }}>
            {fields.map((f) => (
              <Field key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))} />
            ))}
            <Preview form={form} config={config} />
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" disabled={saving} onClick={() => save(false)}>Save as draft</Button>
              <Button type="submit" disabled={saving} className="bg-[#B3261E] text-white hover:bg-[#9E1F19]">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Publish
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this {noun}?</DialogTitle>
            <DialogDescription>
              “{pendingDelete?.title}” will be removed from the website and the apps. This can't be undone — unpublish it instead if you might want it back.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
